import {
  SquaresFour,
  FileText,
  NotePencil,
  UserFocus,
  UsersThree,
  VideoCamera,
  Export,
  Check,
  X,
  ShoppingBag,
} from '../../design-system/icons';
import { formatTierRange } from '../../lib/format';
import { formatMoney } from '../../lib/formatMoney';
import type { Campaign, FieldChange, ProductInput, OfficialAudio } from '@discovr/types';
import { fmtDeliverableType, isAddon, resolvePlatform } from '@discovr/types';
import { CardShell, Section, Field, Pill, PillField, PlatformMark, BriefChip, ReferenceCard, titleCase, formatRegion } from './campaignDetailsParts';
import { MusicNoteSimple } from '@phosphor-icons/react';
import { getFieldDiff, DiffField, DiffPillField } from './editRequestDiff';
import { primaryGoalMeta } from './editFieldMeta';
import { PRODUCTS_FIELD_PATH } from './editRequestFormat';
import { namedProducts } from './editFormState';
import { ProductListReadonly, ProductsDiffValue } from './ProductListReadonly';
// Shared Instagram brand gradient — the coverless-audio placeholder, imported
// from the reporting cell so this field and the "Sound used" cell render the
// identical tile from one source of truth.
import { IG_GRADIENT } from '../../campaign-reporting/SoundUsedCell';
import { officialAudioTrackDisplay } from './officialAudioDisplay';

type OfficialAudioTrack = NonNullable<OfficialAudio['tracks']>[number];

// Instagram audio URL → numeric audio id, so a track can be matched by id
// rather than trusting tracks[]/audio_urls[] positional alignment. Null if none.
function audioIdFromUrl(url: string): string | null {
  return url.match(/\/reels\/audio\/(\d+)/)?.[1] ?? null;
}

// The track for one audio_urls[] entry — matched by id when the URL carries
// one, else by position (see audioIdFromUrl).
function trackForAudioUrl(tracks: OfficialAudioTrack[] | undefined, audioUrl: string, index: number): OfficialAudioTrack | undefined {
  const audioId = audioIdFromUrl(audioUrl);
  return audioId ? tracks?.find((t) => t.audio_id === audioId) : tracks?.[index];
}

// One row of the Campaign audio field: cover, song, artist, linking out to the
// Instagram audio page. A named component (not an inline block-bodied .map()
// callback) so the render logic reads as JSX, not a function with a manual
// `return` buried inside another component's markup.
function OfficialAudioRow({ audioUrl, track }: { audioUrl: string; track: OfficialAudioTrack | undefined }) {
  const { cover, song, artist } = officialAudioTrackDisplay(track);
  return (
    <a
      href={audioUrl}
      target="_blank"
      rel="noreferrer"
      className="inline-flex max-w-full items-center gap-2.5 rounded-[10px] border border-border bg-card px-2.5 py-1.5 hover:bg-muted/40"
    >
      <span
        className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted"
        style={cover ? undefined : { backgroundImage: IG_GRADIENT }}
      >
        {cover ? (
          <img src={cover} alt="" className="size-full object-cover" loading="lazy" />
        ) : (
          <MusicNoteSimple size={14} className="text-white/90" />
        )}
      </span>
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="truncate text-body-sm-medium text-foreground">{song}</span>
        <span className="truncate text-caption-regular text-muted-foreground">{artist}</span>
      </span>
    </a>
  );
}

// Diffed before/after catalog when a products FieldChange is present, else the live catalog.
function ProductsFieldBody({ diff, products }: { diff: ReturnType<typeof getFieldDiff>; products: ProductInput[] }) {
  if (diff) return <ProductsDiffValue oldValue={diff.change.old_value} newValue={diff.change.new_value} />;
  return <ProductListReadonly products={products} />;
}

// Status badge + comment dot for the Products section (the Section header already
// carries the "Products" label, so this only renders the diff decoration row).
function ProductsBadgeRow({ diff }: { diff: ReturnType<typeof getFieldDiff> }) {
  if (!diff) return null;
  return (
    <div className="mt-1 mb-2 flex items-center gap-1.5">
      {diff.statusBadge}
      {diff.commentDot}
    </div>
  );
}

interface CampaignDetailsCardsProps {
  campaign: Campaign;
  /** Empty map renders the plain read view; a non-empty map overlays the
   *  old→new diff (badge + comment dot) per field. */
  diffMap: Map<string, FieldChange>;
  /** The CampaignEditRequest diffMap was built from — required when diffMap is
   *  non-empty, so comment dots know where to post. */
  requestId: string;
  /** field_path -> prior resolved changes on other requests, newest-first;
   *  feeds the comment popover's "Change X/N" pager. Empty is fine. */
  fieldHistory?: Map<string, FieldChange[]>;
  /** Review-status pill label: "Requested" for brand, "Pending" for admin. */
  audience?: 'admin' | 'brand';
}

/** The "Campaign Details" + "Brief" card pair — the campaign's full field
 *  layout, optionally overlaid with a per-field diff via diffMap. Shared so the
 *  admin's live overlay and the brand's Changes-history view render identically. */
export function CampaignDetailsCards({ campaign: c, diffMap, requestId, fieldHistory, audience }: CampaignDetailsCardsProps) {
  const brief = c.brief;
  const dos = brief.dos ?? [];
  const donts = brief.donts ?? [];
  const pref = c.creator_preference;
  const demo = c.targeting.demographics;
  const platform = resolvePlatform(c.platform);
  const isYouTube = platform === 'youtube';

  // Deliverables → content-type + add-on pills (IG). YouTube models deliverables
  // as free text only, so it shows a single description line instead.
  const types = c.deliverables ?? [];
  const contentTypes = types.filter((t) => !isAddon(t, c.platform)).map((t) => fmtDeliverableType(t));
  const addonTypes = types.filter((t) => isAddon(t, c.platform)).map((t) => fmtDeliverableType(t));
  const deliverablesText = c.deliverables_text?.trim();
  // sample_video_url holds one or more reference links, comma-separated.
  const referenceUrls = (brief.sample_video_url ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  // `demo` (targeting.demographics) is who sees the content; `pref`
  // (creator_preference) is who makes it. Two distinct sets — keep them separate.
  const languages = (demo.languages ?? []).map(titleCase);
  // No stored genders means no filter; show the "All" pill (as the create and
  // edit forms do) rather than hiding the field.
  const audienceGenders = (demo.genders ?? []).map(titleCase);
  const creatorGenders = (pref.creator_genders ?? []).map(titleCase);
  // Same icon + label as the edit form's goal picker, so e.g. "sales" renders
  // as "Drive Sales" and the two tabs agree.
  const goal = primaryGoalMeta(c.primary_goal ?? '');
  const audienceGenderPills = audienceGenders.length ? audienceGenders : ['All'];
  const creatorGenderPills = creatorGenders.length ? creatorGenders : ['All'];
  const genderNotes = pref.gender_notes?.trim();
  const regions = (demo.regions ?? []).map(formatRegion);
  const hasAge = demo.age_min > 0 || demo.age_max > 0;
  const gfd = (path: string) => getFieldDiff(diffMap, path, requestId, fieldHistory, audience);
  const nameDiff = gfd('name');
  const products = namedProducts(c.products ?? []);
  const productsDiff = gfd(PRODUCTS_FIELD_PATH);
  const showProducts = products.length > 0 || diffMap.has(PRODUCTS_FIELD_PATH);

  // Near-equal columns per Figma, kept identical to CampaignEditForm's grid so
  // the cards don't resize when the tab flips into edit mode.
  return (
    <div className="grid gap-6 lg:grid-cols-[81fr_83fr]">
      {/* ── Campaign Details ─────────────────────────────────────────────── */}
      <CardShell icon={SquaresFour} title="Campaign Details">
        <div className="space-y-6">
          {/* Campaign basics */}
          <Section icon={NotePencil} title="Campaign basics">
            <div id="field-name" className="col-span-2 min-w-0 scroll-mt-24">
              <div className="mb-1.5 flex items-center gap-1.5 text-body-sm-regular text-muted-foreground">
                <span>Campaign name</span>
                {nameDiff?.statusBadge}
                {nameDiff?.commentDot}
              </div>
              <div className="text-heading-h6 font-medium text-foreground">
                {nameDiff?.value ?? (c.name || <span className="text-muted-foreground">—</span>)}
              </div>
            </div>
            <Field label="Platform">
              <span className="flex items-center gap-2">
                <PlatformMark platform={platform} />
                {titleCase(c.platform)}
              </span>
            </Field>
            <DiffField path="primary_goal" label="Primary goal" diffMap={diffMap} requestId={requestId} fieldHistory={fieldHistory} audience={audience}>
              <span className="flex items-center gap-2">
                <goal.Icon size={18} className="shrink-0 text-muted-foreground" />
                {goal.label || <span className="text-muted-foreground">—</span>}
              </span>
            </DiffField>
            {/* Hide the row when total_budget is unset (undefined); an explicit
                0 still renders. Also show it whenever a diff proposes a
                budget, even with no live value yet. */}
            {(c.total_budget !== undefined || diffMap.has('total_budget')) && (
              <DiffField path="total_budget" label="Total budget" diffMap={diffMap} requestId={requestId} fieldHistory={fieldHistory} audience={audience} currency={c.budget_currency}>
                <span className="text-heading-h7 font-medium">{formatMoney({ amount: c.total_budget ?? NaN, currency: c.budget_currency || 'INR' })}</span>
              </DiffField>
            )}
            <DiffField path="creator_preference.creator_count" label="Creator Count" diffMap={diffMap} requestId={requestId} fieldHistory={fieldHistory} audience={audience}>
              <span className="text-heading-h7 font-medium">{pref.creator_count}</span>
            </DiffField>
            <Field label="Status">
              <Pill>{titleCase(c.status)}</Pill>
            </Field>
            {c.brand_name && <Field label="Brand">{c.brand_name}</Field>}
          </Section>

          {/* Target Audience — who SEES the content (Step 2, first half) */}
          <Section icon={UserFocus} title="Target Audience" divider>
            {(hasAge || diffMap.has('targeting.demographics.age_min') || diffMap.has('targeting.demographics.age_max')) && (
              <Field
                label="Age"
                id="field-targeting.demographics.age_min"
                badge={
                  <>
                    {gfd('targeting.demographics.age_min')?.statusBadge ?? gfd('targeting.demographics.age_max')?.statusBadge}
                    {gfd('targeting.demographics.age_min')?.commentDot ?? gfd('targeting.demographics.age_max')?.commentDot}
                  </>
                }
              >
                {/* age_min and age_max are diffed as two independent changes but
                    share this block; this hidden span gives an age_max-only
                    change its own "Go to field" anchor into the same block. */}
                <span id="field-targeting.demographics.age_max" className="sr-only" aria-hidden="true" />
                {gfd('targeting.demographics.age_min')?.value ??
                  gfd('targeting.demographics.age_max')?.value ?? <Pill>{`${demo.age_min} – ${demo.age_max}`}</Pill>}
              </Field>
            )}
            <DiffPillField path="targeting.demographics.genders" label="Gender" diffMap={diffMap} requestId={requestId} fieldHistory={fieldHistory} audience={audience} values={audienceGenderPills} />
            <DiffPillField path="targeting.demographics.regions" label="Region" diffMap={diffMap} requestId={requestId} fieldHistory={fieldHistory} audience={audience} values={regions} />
            <DiffPillField path="targeting.demographics.languages" label="Language" diffMap={diffMap} requestId={requestId} fieldHistory={fieldHistory} audience={audience} values={languages} />
          </Section>

          {/* Target creators — who MAKES the content (Step 2, second half) */}
          <Section icon={UsersThree} title="Target creators" divider>
            <DiffPillField path="creator_preference.tiers" label="Creator tiers" diffMap={diffMap} requestId={requestId} fieldHistory={fieldHistory} audience={audience} values={(pref.tiers ?? []).map(formatTierRange)} />
            <DiffPillField path="creator_preference.categories" label="Categories" diffMap={diffMap} requestId={requestId} fieldHistory={fieldHistory} audience={audience} values={pref.categories ?? []} />
            <DiffPillField path="creator_preference.creator_genders" label="Creator Gender" diffMap={diffMap} requestId={requestId} fieldHistory={fieldHistory} audience={audience} values={creatorGenderPills} />
            {(genderNotes || diffMap.has('creator_preference.gender_notes')) && (
              <div id="field-creator_preference.gender_notes" className="col-span-2 min-w-0 scroll-mt-24">
                <div className="mb-1.5 flex items-center gap-1.5 text-body-sm-regular text-muted-foreground">
                  <span>Creator gender notes</span>
                  {gfd('creator_preference.gender_notes')?.statusBadge}
                  {gfd('creator_preference.gender_notes')?.commentDot}
                </div>
                {gfd('creator_preference.gender_notes')?.value ?? (
                  <p className="text-body-sm-regular whitespace-pre-wrap text-foreground">{genderNotes}</p>
                )}
              </div>
            )}
          </Section>

          {/* Deliverables & terms — Step 3 + campaign payout */}
          <Section icon={VideoCamera} title="Deliverables & terms" divider>
            <DiffField path="payout_model" label="Payout model" diffMap={diffMap} requestId={requestId} fieldHistory={fieldHistory} audience={audience}>
              {c.payout_model ? (
                <Pill>{titleCase(c.payout_model)}</Pill>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </DiffField>
            {gfd('deliverables') ? (
              <PillField
                label="Content types / Add-ons"
                id="field-deliverables"
                badge={
                  <>
                    {gfd('deliverables')?.statusBadge}
                    {gfd('deliverables')?.commentDot}
                  </>
                }
                values={[]}
                override={gfd('deliverables')?.pills}
              />
            ) : (
              <>
                <PillField label="Content types" values={contentTypes} />
                <PillField label="Add-ons" values={addonTypes} />
              </>
            )}
            {!isYouTube && ((c.usage_rights && c.usage_rights.days > 0) || diffMap.has('usage_rights')) && (
              <DiffField path="usage_rights" label="Usage rights" diffMap={diffMap} requestId={requestId} fieldHistory={fieldHistory} audience={audience}>
                <Pill>{c.usage_rights?.days ?? 0} days</Pill>
              </DiffField>
            )}
            {/* Campaign audio (Official Audio Compliance) — Instagram only, shown
                right after Usage rights. Renders like the reporting "Sound used"
                cell (cover/song/artist resolved and stored at Verify time).
                diffMap.has(...) mirrors Usage rights above: a pending "add audio"
                request has nothing on the live campaign to render, so without
                this gate the field would silently disappear instead of showing
                the pending diff. */}
            {!isYouTube && (!!c.official_audio?.audio_urls?.length || diffMap.has('official_audio')) && (
              <DiffField path="official_audio" label="Campaign audio" diffMap={diffMap} requestId={requestId} fieldHistory={fieldHistory} audience={audience}>
                <div className="flex flex-col gap-2">
                  {c.official_audio?.audio_urls?.map((audioUrl, i) => (
                    <OfficialAudioRow
                      key={audioUrl}
                      audioUrl={audioUrl}
                      track={trackForAudioUrl(c.official_audio?.tracks, audioUrl, i)}
                    />
                  ))}
                </div>
              </DiffField>
            )}
            {(deliverablesText || diffMap.has('deliverables_text')) && (
              <div id="field-deliverables_text" className="col-span-2 min-w-0 scroll-mt-24">
                <div className="mb-1.5 flex items-center gap-1.5 text-body-sm-regular text-muted-foreground">
                  <span>Deliverables description</span>
                  {gfd('deliverables_text')?.statusBadge}
                  {gfd('deliverables_text')?.commentDot}
                </div>
                {gfd('deliverables_text')?.value ?? (
                  <p className="text-body-sm-regular whitespace-pre-wrap text-foreground">{deliverablesText}</p>
                )}
              </div>
            )}
          </Section>

          {/* Products — shown once the campaign has >= 1 named product, or a
              pending/rejected diff proposes one. */}
          {showProducts && (
            <Section icon={ShoppingBag} title="Products" divider>
              <div id={`field-${PRODUCTS_FIELD_PATH}`} className="col-span-2 min-w-0 scroll-mt-24">
                <ProductsBadgeRow diff={productsDiff} />
                <ProductsFieldBody diff={productsDiff} products={products} />
              </div>
            </Section>
          )}
        </div>
      </CardShell>

      {/* ── Brief ────────────────────────────────────────────────────────── */}
      <CardShell icon={FileText} title="Brief">
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-x-6 gap-y-5">
            {(brief.video_title || diffMap.has('brief.video_title')) && (
              <DiffField path="brief.video_title" label="Brief title" diffMap={diffMap} requestId={requestId} fieldHistory={fieldHistory} audience={audience}>
                {brief.video_title}
              </DiffField>
            )}
            {(brief.cta || diffMap.has('brief.cta')) && (
              <DiffField path="brief.cta" label="Call to action" diffMap={diffMap} requestId={requestId} fieldHistory={fieldHistory} audience={audience}>
                <span className="inline-flex items-center gap-1.5">{brief.cta}</span>
              </DiffField>
            )}
          </div>

          {(c.description || diffMap.has('description')) && (
            <div id="field-description" className="scroll-mt-24">
              <div className="mb-1.5 flex items-center gap-1.5 text-caption-medium tracking-wide text-muted-foreground uppercase">
                <span>Description</span>
                {gfd('description')?.statusBadge}
                {gfd('description')?.commentDot}
              </div>
              {gfd('description')?.value ?? (
                <p className="text-body-sm-regular leading-relaxed text-foreground">{c.description}</p>
              )}
            </div>
          )}

          {(brief.video_description || diffMap.has('brief.video_description')) && (
            <div id="field-brief.video_description" className="scroll-mt-24">
              <div className="mb-1.5 flex items-center gap-1.5 text-caption-medium tracking-wide text-muted-foreground uppercase">
                <span>Brief description</span>
                {gfd('brief.video_description')?.statusBadge}
                {gfd('brief.video_description')?.commentDot}
              </div>
              {gfd('brief.video_description')?.value ?? (
                <p className="text-body-sm-regular leading-relaxed text-foreground">{brief.video_description}</p>
              )}
            </div>
          )}

          {(brief.primary_focus || brief.secondary_focus || diffMap.has('brief.primary_focus') || diffMap.has('brief.secondary_focus')) && (
            <div className="grid grid-cols-2 gap-x-6 gap-y-5">
              {(brief.primary_focus || diffMap.has('brief.primary_focus')) && (
                <DiffField path="brief.primary_focus" label="Primary focus" diffMap={diffMap} requestId={requestId} fieldHistory={fieldHistory} audience={audience}>
                  <span className="text-body-sm-regular leading-relaxed text-foreground">{brief.primary_focus}</span>
                </DiffField>
              )}
              {(brief.secondary_focus || diffMap.has('brief.secondary_focus')) && (
                <DiffField path="brief.secondary_focus" label="Secondary focus" diffMap={diffMap} requestId={requestId} fieldHistory={fieldHistory} audience={audience}>
                  <span className="text-body-sm-regular leading-relaxed text-foreground">{brief.secondary_focus}</span>
                </DiffField>
              )}
            </div>
          )}

          <div>
            <div className="mb-1.5 text-body-sm-regular text-muted-foreground">Disclosure required</div>
            <span className="text-body-sm-regular leading-relaxed text-foreground">
              {typeof brief.requires_disclosure === 'boolean'
                ? brief.requires_disclosure ? 'Yes' : 'No'
                : 'Yes (default)'}
            </span>
          </div>

          {(dos.length > 0 || donts.length > 0 || diffMap.has('brief.dos') || diffMap.has('brief.donts')) && (
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <div id="field-brief.dos" className="space-y-2.5 scroll-mt-24">
                <div className="flex items-center gap-1.5 text-body-sm-medium tracking-wide text-emerald-600 uppercase">
                  <Check size={16} weight="bold" />
                  Do&apos;s
                  {gfd('brief.dos')?.statusBadge}
                  {gfd('brief.dos')?.commentDot}
                </div>
                {gfd('brief.dos') ? (
                  gfd('brief.dos')?.pills
                ) : dos.length > 0 ? (
                  <div className="space-y-2">
                    {dos.map((item, i) => (
                      <BriefChip key={i}>{item}</BriefChip>
                    ))}
                  </div>
                ) : (
                  <span className="text-body-sm-regular text-muted-foreground">—</span>
                )}
              </div>
              <div id="field-brief.donts" className="space-y-2.5 scroll-mt-24">
                <div className="flex items-center gap-1.5 text-body-sm-medium tracking-wide text-rose-600 uppercase">
                  <X size={16} weight="bold" />
                  Don&apos;ts
                  {gfd('brief.donts')?.statusBadge}
                  {gfd('brief.donts')?.commentDot}
                </div>
                {gfd('brief.donts') ? (
                  gfd('brief.donts')?.pills
                ) : donts.length > 0 ? (
                  <div className="space-y-2">
                    {donts.map((item, i) => (
                      <BriefChip key={i}>{item}</BriefChip>
                    ))}
                  </div>
                ) : (
                  <span className="text-body-sm-regular text-muted-foreground">—</span>
                )}
              </div>
            </div>
          )}

          {(brief.sample_video_url || diffMap.has('brief.sample_video_url')) && (
            <div id="field-brief.sample_video_url" className="scroll-mt-24">
              <div className="mb-3 flex items-center gap-1.5 text-caption-medium tracking-wide text-muted-foreground uppercase">
                <Export size={14} className="shrink-0" />
                <span>Added · {referenceUrls.length} Reference{referenceUrls.length === 1 ? '' : 's'}</span>
                {gfd('brief.sample_video_url')?.statusBadge}
                {gfd('brief.sample_video_url')?.commentDot}
              </div>
              {gfd('brief.sample_video_url') ? (
                <div className="rounded-md bg-muted px-2.5 py-2 text-body-sm-regular">
                  {gfd('brief.sample_video_url')?.value}
                </div>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {referenceUrls.map((u, i) => (
                    <ReferenceCard key={`${u}-${i}`} url={u} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </CardShell>
    </div>
  );
}
